import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  UserCheck,
  FileCheck,
  Search,
  Send,
  DollarSign,
  ArrowRight,
  ArrowLeft,
  X,
  Sparkles,
  CheckCircle2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function OnboardingTour({ onComplete }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  const steps = [
    {
      icon: Sparkles,
      iconBg: "bg-[#D4AF37]/15",
      iconColor: "text-[#D4AF37]",
      title: t("tour.welcomeTitle"),
      subtitle: t("tour.welcomeSubtitle"),
      body: t("tour.welcomeBody"),
    },
    {
      icon: UserCheck,
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
      title: t("tour.profileTitle"),
      subtitle: t("tour.profileSubtitle"),
      body: t("tour.profileBody"),
    },
    {
      icon: FileCheck,
      iconBg: "bg-emerald-100",
      iconColor: "text-emerald-600",
      title: t("tour.verifyTitle"),
      subtitle: t("tour.verifySubtitle"),
      body: t("tour.verifyBody"),
    },
    {
      icon: Search,
      iconBg: "bg-purple-100",
      iconColor: "text-purple-600",
      title: t("tour.browseTitle"),
      subtitle: t("tour.browseSubtitle"),
      body: t("tour.browseBody"),
    },
    {
      icon: Send,
      iconBg: "bg-orange-100",
      iconColor: "text-orange-600",
      title: t("tour.bidTitle"),
      subtitle: t("tour.bidSubtitle"),
      body: t("tour.bidBody"),
    },
    {
      icon: DollarSign,
      iconBg: "bg-green-100",
      iconColor: "text-green-600",
      title: t("tour.paidTitle"),
      subtitle: t("tour.paidSubtitle"),
      body: t("tour.paidBody"),
    },
  ];

  const current = steps[step];
  const isLast = step === steps.length - 1;
  const IconComponent = current.icon;

  const handleFinish = async () => {
    try {
      await axios.put(`${API}/auth/tour-complete`);
    } catch {}
    onComplete();
  };

  const handleSkip = async () => {
    try {
      await axios.put(`${API}/auth/tour-complete`);
    } catch {}
    onComplete();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="onboarding-tour">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        {/* Skip button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-slate-100 transition-colors z-10"
          data-testid="tour-skip-btn"
        >
          <X className="w-4 h-4 text-slate-400" />
        </button>

        {/* Step indicator */}
        <div className="flex gap-1.5 px-6 pt-6">
          {steps.map((_, i) => (
            <div
              key={`step-${i}`}
              className={`h-1 flex-1 rounded-full transition-colors ${
                i <= step ? "bg-[#0B1F3B]" : "bg-slate-200"
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="px-6 pt-8 pb-6 text-center">
          <div className={`w-16 h-16 rounded-2xl ${current.iconBg} flex items-center justify-center mx-auto mb-5`}>
            <IconComponent className={`w-8 h-8 ${current.iconColor}`} />
          </div>

          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
            {t("tour.step")} {step + 1} / {steps.length}
          </p>

          <h2 className="text-xl font-extrabold text-[#0B1F3B] mb-2" style={{ fontFamily: "Manrope" }}>
            {current.title}
          </h2>

          <p className="text-sm font-medium text-[#D4AF37] mb-3">
            {current.subtitle}
          </p>

          <p className="text-sm text-slate-600 leading-relaxed">
            {current.body}
          </p>
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 flex items-center gap-3">
          {step > 0 && (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="h-11 px-4"
              data-testid="tour-prev-btn"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
          )}

          {isLast ? (
            <Button
              onClick={handleFinish}
              className="flex-1 h-11 bg-[#D4AF37] hover:bg-[#C4A030] text-[#0B1F3B] font-bold"
              data-testid="tour-finish-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {t("tour.getStarted")}
            </Button>
          ) : (
            <Button
              onClick={() => setStep(step + 1)}
              className="flex-1 h-11 bg-[#0B1F3B] hover:bg-[#162A4C] text-white font-bold"
              data-testid="tour-next-btn"
            >
              {t("tour.next")}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
